import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import TextArea from "../../../../../lib/html5-software-library/src/ranj/components/text/TextArea";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import Config from "../../../core/config/Config";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import NGTConfigKey from "../../../ngt/base/NGTConfigKey";

export default class ConversationInputPanelMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher
     * @param {RevisableLanguagePack} languagePack 
     * @param {LayoutManager} layoutManager 
     * @param {OverlayFactory} overlayFactory 
     * @param {Config} config 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, overlayFactory, languagePack, config, soundManager) {
        super(target, dispatcher, layoutManager, overlayFactory);

        this._languagePack = languagePack;
        this._config = config;
        this._soundManager = soundManager;

        /** @type {createjs.EventDispatcher} */
        this._eventDispatcher = new createjs.EventDispatcher();
        this._enabled = true;

        this._handleKeyPressedBound = this._handleKeyPressed.bind(this);
    }

    initialize() {
        super.initialize();

        window.addEventListener('keypress', this._handleKeyPressedBound);

        const inputFieldBounds = this._target.inputText.getBounds();
        this._inputFieldTextArea = new TextArea(this._target.inputText, inputFieldBounds.width, inputFieldBounds.height, 'gameContainer', this._config.getValue(NGTConfigKey.MAX_CHARACTER_INPUT_PER_MESSAGE));
        this._inputFieldTextArea.setTextChangedCallback(this._handleTextChanged.bind(this));

        this._sendButton = this.createButton(this._target.sendButton, this._handleSendButton.bind(this), null, SoundType.BUTTON_CLICK);
        this._sendButton.setEnabled(false);

        // Clear placeholder text. A full text field is required for bounds calculation.
        this._target.inputText.text = "";
        this._inputFieldTextArea.setText("");
        this._inputFieldTextArea.setDefaultText(this._languagePack.getText(`conversationScreen.inputPlaceholder`));

        this.initialY = this._target.y;
    }

    destroy() {
        super.destroy();
        this._inputFieldTextArea.destroy();

        this._eventDispatcher.removeAllEventListeners();

        window.removeEventListener('keypress', this._handleKeyPressedBound);
    }

    _handleSendButton() {
        const currentText = this._inputFieldTextArea.getText().trim()

        if (currentText.length === 0) {
            return;
        }

        const playerInputEvent = new createjs.Event(ConversationInputPanelMediator.PLAYER_INPUT_EVENT);
        playerInputEvent.playerInput = currentText;
        this._eventDispatcher.dispatchEvent(playerInputEvent);

        this._inputFieldTextArea.setText("");
        this._handleTextChanged();

        setTimeout(() => { this._inputFieldTextArea.setText(""); }, 0) // Reset the text next frame so that the enter also gets removed.
    }

    setEnabled(value) {
        this._enabled = value;
        this._inputFieldTextArea.setEnabled(value);
    }

    getEnabled() {
        return this._enabled;
    }

    focusInputField() {
        this._inputFieldTextArea.focus();
    }

    blurInputField() {
        this._inputFieldTextArea.blur();
    }

    // Disable the send button when the input field is empty
    _handleTextChanged() {
        const currentText = this._inputFieldTextArea.getText();
        this._inputFieldTextArea.setText(currentText);
        if (currentText?.trim().length === 0) {
            this._sendButton.setEnabled(false);
        } else {
            this._sendButton.setEnabled(true);
        }
    }

    _handleKeyPressed(evt) {
        // If the enter key is pressed
        if (evt.charCode === 13) {
            this.playSound(SoundType.BUTTON_CLICK);
            this._handleSendButton();
            return;
        }

        const typeEvent = new createjs.Event(ConversationInputPanelMediator.PLAYER_TYPE_EVENT);
        this.eventDispatcher.dispatchEvent(typeEvent);
    }

    async show(delayInMs = 0) {
        const wasAlreadyShown = this._target.alpha > .1;
        const playSound = !wasAlreadyShown;
        this._inputFieldTextArea.setVisible(true);
        await createjs.Tween.get(this._target, { override: true })
            .wait(delayInMs)
            .call(() => {
                if (playSound) {
                    this._soundManager.playSound(SoundType.PLAYER_INPUT_PANEL_APPEARS);
                }
            })
            .to({
                y: this.initialY,
                alpha: 1,
            }, 450, createjs.Ease.backOut)
            .toPromise();
    }

    async hide(delayInMs = 0) {
        await createjs.Tween.get(this._target, { override: true })
            .wait(delayInMs)
            .to({
                alpha: 0,
            }, 450, createjs.Ease.sineOut)
            .call(() => {
                this._inputFieldTextArea.setVisible(false); // Hide the input field, so that it doesn't eat clicks.
            })
            .toPromise();
    }

    hideInstant() {
        this._target.alpha = 0;
        this._target.y = this.initialY + 200;
        this._target.alpha = 0;
    }

    get eventDispatcher() {
        return this._eventDispatcher;
    }
}

ConversationInputPanelMediator.PLAYER_INPUT_EVENT = `ConversationInputPanelMediator.PLAYER_INPUT_EVENT`;
ConversationInputPanelMediator.PLAYER_TYPE_EVENT = `ConversationInputPanelMediator.PLAYER_TYPE_EVENT`;